import supabase from "./client";

const TABLE_NAME =
  process.env.REACT_APP_SUPABASE_FIRESIDE_CHATS_TABLE || "fireside_chats";
const CAREER_CATEGORIES_TABLE =
  process.env.REACT_APP_SUPABASE_CAREER_CATEGORIES_TABLE || "career_categories";
const LIST_SELECT_COLUMNS = [
  "id",
  "name",
  "subtitle",
  "web_url",
  "province_territory",
  "indigenous_community",
  "illustration",
  "updated_at",
  "broad_based_tech_areas",
  "career_category"
].join(",");

const DETAIL_SELECT_COLUMNS = "*";

// Map of legacy Airtable record IDs (stored on fireside_chats.career_category)
// to category names. The career_categories table in Supabase uses a different
// set of airtable_ids, so the DB join doesn't resolve these. Fixes the blank
// top-right badge on each card.
const LEGACY_CATEGORY_NAMES = {
  reckUwPPniUpbLPfU: "Activism",
  recsfoUNjHnbMmP00: "Arts / Graphic Arts",
  reccWSAhdO9GikSvA: "Athletes / Sports and Recreation",
  rec8BvXjyzAOrzGvl: "Business & Entrepreneurship",
  rec0QBAmWAbz2pYEj: "Community Development",
  recfFyxrDB0mhjhtU: "Computer Science",
  recly4N8rU7aoH2IN: "Culture",
  receRnWSVQ4CSUZRF: "Education",
  reclXV5r3I7sXR5v2: "Elected Officials / Politics",
  recDHQSMrOXZLVq5E: "Entertainment",
  recixmrxhGB2i122a: "Government & Policy",
  recKp0wD5w44HFLkI: "Health & Science",
  recSOXHafHdJJlWaH: "Skilled Trades"
};

const FIELD_ALIASES = {
  Name: ["Name", "name"],
  "Broad-based Technology Area": [
    "Broad-based Technology Area",
    "broad_based_technology_area",
    "broad_based_tech_areas"
  ],
  "Category Text": ["Category Text", "category_text"],
  "Indigenous Community": ["Indigenous Community", "indigenous_community"],
  "P/T Name": [
    "P/T Name",
    "province_territory_name",
    "pt_name",
    "province_territory"
  ],
  Subtitle: ["Subtitle", "subtitle"],
  Bio: ["Bio", "bio", "story"],
  "Illustration File": [
    "Illustration File",
    "illustration_file",
    "illustration_url",
    "illustration_file_url",
    "illustration"
  ],
  "Date Web URL Updated": [
    "Date Web URL Updated",
    "date_web_url_updated",
    "updated_at"
  ],
  "Video Link": ["Video Link", "video_link"],
  "Web URL": ["Web URL", "web_url"],
  "Interview Status": ["Interview Status", "interview_status"]
};

const getValue = (row, keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return row[key];
    }
  }

  return undefined;
};

const toArray = value => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value !== "string") {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (error) {
      console.error("Unable to parse Supabase array field", error);
    }
  }

  return trimmed
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);
};

const toIllustrationFiles = value => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map(item => {
      if (typeof item === "string") {
        return {
          url: item,
          thumbnails: {
            large: {
              url: item
            }
          }
        };
      }

      if (item?.url && !item?.thumbnails?.large?.url) {
        return {
          ...item,
          thumbnails: {
            ...(item.thumbnails || {}),
            large: {
              url: item.url
            }
          }
        };
      }

      return item;
    });
  }

  if (typeof value === "string") {
    return [
      {
        url: value,
        thumbnails: {
          large: {
            url: value
          }
        }
      }
    ];
  }

  if (value?.url) {
    return [
      {
        ...value,
        thumbnails: {
          ...(value.thumbnails || {}),
          large: {
            url: value.url
          }
        }
      }
    ];
  }

  return [];
};

const normalizeChat = row => ({
  id: row.id,
  Name: getValue(row, FIELD_ALIASES.Name) || "",
  "Broad-based Technology Area": toArray(
    getValue(row, FIELD_ALIASES["Broad-based Technology Area"])
  ),
  "Category Text": getValue(row, FIELD_ALIASES["Category Text"]) || "",
  "Indigenous Community": toArray(
    getValue(row, FIELD_ALIASES["Indigenous Community"])
  ),
  "P/T Name": getValue(row, FIELD_ALIASES["P/T Name"]) || "",
  Subtitle: getValue(row, FIELD_ALIASES.Subtitle) || "",
  Bio: getValue(row, FIELD_ALIASES.Bio) || "",
  "Illustration File": toIllustrationFiles(
    getValue(row, FIELD_ALIASES["Illustration File"])
  ),
  "Date Web URL Updated":
    getValue(row, FIELD_ALIASES["Date Web URL Updated"]) || null,
  "Video Link": getValue(row, FIELD_ALIASES["Video Link"]) || "",
  "Web URL": getValue(row, FIELD_ALIASES["Web URL"]) || "",
  "Interview Status": getValue(row, FIELD_ALIASES["Interview Status"]) || null,
  career_category: toArray(row.career_category)
});

const isCompletedChat = chat => {
  if (!chat["Interview Status"]) {
    return true;
  }

  return chat["Interview Status"] === "Completed";
};

const sortByName = chats =>
  [...chats].sort((left, right) => left.Name.localeCompare(right.Name));

const fetchChats = async (selectColumns, customizeQuery) => {
  if (!supabase) {
    throw new Error(
      "Supabase is not configured. Set REACT_APP_SUPABASE_ANON_KEY or REACT_APP_CN_SUPABASE_ANON_KEY."
    );
  }

  let query = supabase
    .from(TABLE_NAME)
    .select(selectColumns)
    .order("name", { ascending: true });

  if (customizeQuery) {
    query = customizeQuery(query);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  const chats = (data || []).map(normalizeChat);
  const categoryIds = [...new Set(chats.flatMap(chat => chat.career_category || []))];

  if (!categoryIds.length) {
    return chats;
  }

  const unresolvedIds = categoryIds.filter(id => !LEGACY_CATEGORY_NAMES[id]);
  const categoryNameById = new Map();

  if (unresolvedIds.length) {
    const { data: categoryRows, error: categoriesError } = await supabase
      .from(CAREER_CATEGORIES_TABLE)
      .select("airtable_id, name")
      .in("airtable_id", unresolvedIds);

    if (categoriesError) {
      throw categoriesError;
    }

    (categoryRows || []).forEach(category => {
      categoryNameById.set(category.airtable_id, category.name);
    });
  }

  return chats.map(chat => {
    const categoryNames = (chat.career_category || [])
      .map(categoryId =>
        LEGACY_CATEGORY_NAMES[categoryId] || categoryNameById.get(categoryId)
      )
      .filter(Boolean);

    return {
      ...chat,
      "Category Text": categoryNames.join(", ")
    };
  });
};

const matchesName = (chat, name) => {
  const normalizedName = chat.Name.toLowerCase();
  const parts = Array.isArray(name) ? name : [name];

  return parts.every(part => normalizedName.includes(String(part).toLowerCase()));
};

export class FiresideChatsTable {
  getAllCompleted = async () => {
    const chats = await fetchChats(LIST_SELECT_COLUMNS, query =>
      query
        .not("broad_based_tech_areas", "is", null)
        .not("illustration", "is", null)
    );
    return chats.filter(isCompletedChat);
  };

  getByName = async name => {
    const chats = await fetchChats(DETAIL_SELECT_COLUMNS);
    return sortByName(chats.filter(chat => isCompletedChat(chat) && matchesName(chat, name)));
  };
}
